import { useEffect, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MapPin, Phone, Mail, Globe, Heart } from 'lucide-react';
import { usePropertyContext, useWidgetSettings } from '@/context/PropertyContext';
import { Header } from './Header';
import { DemoBanner } from './DemoBanner';

interface LandingLayoutProps {
  children: ReactNode;
}

/**
 * Convert hex color to HSL values for CSS variable
 */
function hexToHsl(hex: string): string {
  hex = hex.replace('#', '');

  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * LandingLayout - Premium full page accessible layout
 * 
 * Features:
 * - Skip to main content link
 * - Elegant footer with refined typography
 * - Custom branding support
 * - Full WCAG 2.1 AA compliance
 */
export function LandingLayout({ children }: LandingLayoutProps) {
  const { config, propertyKey } = usePropertyContext();
  const settings = useWidgetSettings();
  const [searchParams] = useSearchParams();

  // Apply custom colors from property settings
  useEffect(() => {
    if (settings.primaryColor) {
      document.documentElement.style.setProperty('--primary', hexToHsl(settings.primaryColor));
    }
    // Apply success color if provided (for check marks, success states)
    if ('successColor' in settings && typeof settings.successColor === 'string') {
      document.documentElement.style.setProperty('--success', hexToHsl(settings.successColor));
    }
    // Apply warning color if provided (for demo mode, warnings)
    if ('warningColor' in settings && typeof settings.warningColor === 'string') {
      document.documentElement.style.setProperty('--warning', hexToHsl(settings.warningColor));
    }
    // Apply rating color if provided (for star ratings)
    if ('ratingColor' in settings && typeof settings.ratingColor === 'string') {
      document.documentElement.style.setProperty('--rating', hexToHsl(settings.ratingColor));
    }
  }, [settings]);

  // Set document title for accessibility
  useEffect(() => {
    const propertyName = config?.property?.name || 'Experiences';
    document.title = `Book Experiences - ${propertyName}`;
  }, [config?.property?.name]);

  const property = config?.property;
  const contact = property?.contact;
  const location = property?.location;

  // Build category links with property param preserved
  const buildCategoryLink = (categorySlug: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('category', categorySlug);
    if (propertyKey && !params.has('property')) {
      params.set('property', propertyKey);
    }
    return `/?${params.toString()}`;
  };

  return (
    <div className="flex min-h-screen flex-col bg-background" lang="en">
      {/* Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-xl focus:shadow-lg focus:font-medium"
      >
        Skip to main content
      </a>

      <DemoBanner />
      <Header />

      <main
        id="main-content"
        className="flex-1"
        role="main"
        aria-label={`Experiences at ${property?.name || 'this property'}`}
      >
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          {children}
        </div>
      </main>

      {/* Premium Footer */}
      <footer
        className="border-t border-border/40 bg-gradient-to-b from-muted/20 to-muted/40"
        role="contentinfo"
        aria-label="Site footer"
      >
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="grid gap-12 md:grid-cols-3 lg:gap-20">
            {/* Property Info */}
            <div>
              <h2 className="font-serif text-2xl font-semibold text-foreground tracking-luxury">{property?.name}</h2>
              {property?.description && (
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{property.description}</p>
              )}
            </div>

            {/* Contact Info - Refined layout */}
            <div>
              <h2 className="text-xs font-bold uppercase tracking-elegant text-muted-foreground mb-5">Contact</h2>
              <ul className="space-y-4" aria-label="Contact information">
                {location?.address && (
                  <li className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-muted/60">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <address className="not-italic text-sm text-muted-foreground leading-relaxed">
                      {location.address}
                      {location.city && <br />}
                      {location.city}{location.state && `, ${location.state}`}
                    </address>
                  </li>
                )}
                {contact?.phone && (
                  <li className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/60">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <a
                      href={`tel:${contact.phone}`}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                      aria-label={`Call us at ${contact.phone}`}
                    >
                      {contact.phone}
                    </a>
                  </li>
                )}
                {contact?.email && (
                  <li className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/60">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <a
                      href={`mailto:${contact.email}`}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                      aria-label={`Email us at ${contact.email}`}
                    >
                      {contact.email}
                    </a>
                  </li>
                )}
                {contact?.website && (
                  <li className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/60">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <a
                      href={contact.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                      aria-label="Visit our website (opens in new tab)"
                    >
                      Visit Website
                      <span className="sr-only"> (opens in new tab)</span>
                    </a>
                  </li>
                )}
              </ul>
            </div>

            {/* Quick Links - Elegant category links */}
            <nav aria-label="Experience categories">
              <h2 className="text-xs font-bold uppercase tracking-elegant text-muted-foreground mb-5">Experiences</h2>
              <ul className="space-y-3">
                {config?.categories.slice(0, 5).map((category) => (
                  <li key={category.id}>
                    <Link
                      to={buildCategoryLink(category.slug)}
                      className="inline-flex items-center gap-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded py-1"
                    >
                      {category.icon && <span aria-hidden="true" className="text-base">{category.icon}</span>}
                      {category.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Footer Bottom - Refined separator */}
          <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-border/40 pt-10 sm:flex-row">
            <p className="text-sm text-muted-foreground font-medium">
              &copy; {new Date().getFullYear()} {property?.name}. All rights reserved.
            </p>

            {settings.showPoweredBy && (
              <a
                href="https://experiencelocal.io"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
              >
                <Heart className="h-3.5 w-3.5 text-primary" />
                <span className="font-medium">Powered by ExperienceLocal</span>
                <span className="sr-only"> (opens in new tab)</span>
              </a>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
